import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { listDocuments } from '../../../../services/document';
import { useAgent } from './AgentContext';

/**
 * DocumentsContext - Maneja los documentos del VAULT PROPIO del agente
 * 
 * Este contexto contiene:
 * - Documentos que están dentro del vault propio del agente (agent.vaultUuid)
 * 
 * Se muestran en la tab "Documents" del KnowledgePage
 * 
 * NOTA: Esto es diferente a los "Agent Documents" que están en DrivesContext
 * (documentos asignados directamente al agente).
 */

const DocumentsContext = createContext(null);

export const DocumentsProvider = ({ children }) => {
  const { agent } = useAgent();
  const vaultUuid = agent?.vaultUuid;

  // Estado
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Ref para rastrear el vaultUuid anterior y detectar cambios
  const previousVaultUuidRef = useRef(vaultUuid);

  const normalizeDocument = useCallback((doc) => {
    const fileName = doc.name || '';
    const extractedExtension = doc.extension || (fileName.includes('.') 
      ? fileName.split('.').pop().toLowerCase() 
      : null);
    
    const getTypeFromExtension = (ext) => {
      if (!ext) return null;
      const extLower = ext.toLowerCase();
      if (['pdf'].includes(extLower)) return 'application/pdf';
      if (['doc', 'docx'].includes(extLower)) return 'application/msword';
      if (['xls', 'xlsx'].includes(extLower)) return 'application/vnd.ms-excel';
      if (['ppt', 'pptx'].includes(extLower)) return 'application/vnd.ms-powerpoint';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extLower)) return 'image';
      return null;
    };
    
    return {
      ...doc,
      uuid: doc.documentUuid || doc.uuid || doc.id,
      id: doc.documentUuid || doc.uuid || doc.id,
      created_at: doc.createdAt || doc.created_at,
      createdAt: doc.createdAt || doc.created_at,
      updated_at: doc.updatedAt || doc.updated_at,
      updatedAt: doc.updatedAt || doc.updated_at,
      owner: doc.ownerFullName || doc.owner || doc.createdBy || doc.uploadedBy || 'Unknown',
      createdBy: doc.ownerFullName || doc.owner || doc.createdBy || doc.uploadedBy || 'Unknown',
      state: doc.status || doc.state || null,
      name: doc.name || 'Unnamed Document',
      description: doc.description || null,
      type: doc.type || getTypeFromExtension(extractedExtension),
      extension: extractedExtension,
      size: doc.size || null,
      metadata: doc.metadata || {},
    };
  }, []);

  // Fetch documents del vault propio del agente
  const fetchDocuments = useCallback(async (force = false) => {
    if (!vaultUuid) {
      console.log('DocumentsContext: No vaultUuid available, skipping fetch');
      return;
    }
    
    // Evitar refetch si ya se cargaron (a menos que sea forzado)
    if (hasFetched && !force) return;

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        vault_uuid: vaultUuid,
        page_size: 100, // Cargar todos
      };

      const response = await listDocuments(requestBody);
      
      const rawDocuments = response.data?.body || response.data?.data?.body || [];
      const documentsList = rawDocuments.map(normalizeDocument);
      
      setDocuments(documentsList);
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching vault documents:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [vaultUuid, hasFetched, normalizeDocument]);

  const addDocument = useCallback((document) => {
    const normalizedDoc = normalizeDocument(document);
    setDocuments((prev) => [normalizedDoc, ...prev]);
  }, [normalizeDocument]);

  // Remover documento
  const removeDocument = useCallback((documentId) => {
    setDocuments((prev) => prev.filter((d) => 
      (d.uuid || d.id) !== documentId
    ));
  }, []);

  // Actualizar documento
  const updateDocument = useCallback((documentId, updates) => {
    setDocuments((prev) => prev.map((d) => {
      const id = d.uuid || d.id;
      return id === documentId ? { ...d, ...updates } : d;
    }));
  }, []);

  // Refetch forzado
  const refetch = useCallback(() => {
    return fetchDocuments(true);
  }, [fetchDocuments]);

  // Reset del estado
  const reset = useCallback(() => {
    setDocuments([]);
    setLoading(false);
    setError(null);
    setHasFetched(false);
  }, []);

  // Resetear y recargar cuando cambia el vaultUuid (nuevo agente)
  useEffect(() => {
    const previousVaultUuid = previousVaultUuidRef.current;
    
    // Si cambió el vaultUuid, resetear estado y recargar
    if (previousVaultUuid !== vaultUuid) {
      previousVaultUuidRef.current = vaultUuid;
      
      // Resetear estado
      reset();
      
      // Recargar documentos del nuevo vault si está disponible
      if (vaultUuid) {
        fetchDocuments(true);
      }
    }
  }, [vaultUuid, reset, fetchDocuments]);

  const value = useMemo(() => ({
    // Estado
    documents,
    loading,
    error,
    hasFetched,
    vaultUuid, // Exponemos el vaultUuid para referencia
    
    // Acciones
    fetchDocuments,
    addDocument,
    removeDocument,
    updateDocument,
    refetch,
    reset,
    
    // Computed
    documentsCount: documents.length,
  }), [
    documents,
    loading,
    error,
    hasFetched,
    vaultUuid,
    fetchDocuments,
    addDocument,
    removeDocument,
    updateDocument,
    refetch,
    reset,
  ]);

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments must be used within DocumentsProvider');
  }
  return context;
};

export default DocumentsContext;
